<template>
  <div class="loading" v-if="loading">
    <div class="spinner"></div>
    <p>Loading Host Interface...</p>
  </div>


  <!-- Main Content -->
  <div v-if="!loading" class="ui-center">
    <!-- Configuration Step -->
    <PartyConfiguration
      v-if="currentStep === 'configuration'"
      ref="partyConfigRef"
      :sample-config="sampleConfig"
      @validate="handleValidate"
    />

    <!-- Party Active Step -->
    <PartyActive
      v-if="currentStep === 'party-active'"
      :party-data="partyData"
      :join-url="joinUrl"
      :tv-join-url="tvJoinUrl"
      :connected-players="connectedPlayers"
      :is-connected="isConnected"
      :all-players-ready="allPlayersReady()"
      @start-game="startGame"
      @test-party-ops="testPartyOperations"
      @end-party="endParty"
    />

    <!-- Game Running Step (Game Management) -->
    <GameManager
      v-if="currentStep === 'game-running'"
      ref="gameManagerRef"
      :party-data="partyData"
      :connected-players="connectedPlayers"
      :signaling="signaling"
      :active-game-index="activeGameIndex"
      :current-screen-index="currentScreenIndex"
      :completed-games="completedGames"
      :answer-stats="answerStats"
      :host-signaling="{
        startGame: hostStartGameWrapper,
        endGame: hostEndGameWrapper,
        changeView,
        displayScoreUpdate,
        nextScreen: hostNextScreen,
        previousScreen: hostPreviousScreen,
        realTimeScoring: realTimeScoring,
        autoReveal: autoReveal,
        revealAnswers: revealAnswers
      }"
      @back-to-party="goToStep('party-active')"
      @start-game="handleGameStart"
      @end-game="handleGameEnd"
      @change-view="handleViewChange"
      @update-real-time-scoring="handleRealTimeScoringUpdate"
      @update-auto-reveal="handleAutoRevealUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useHostSignaling } from './composables/useHostSignaling'
import { usePartyState } from './composables/usePartyState'
import PartyConfiguration from './components/PartyConfiguration.vue'
import PartyActive from './components/PartyActive.vue'
import GameManager from './components/GameManager.vue'

// Host state
const loading = ref(true)
const hostId = ref('host-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9))
const partyConfigRef = ref<InstanceType<typeof PartyConfiguration> | null>(null)
const gameManagerRef = ref<InstanceType<typeof GameManager> | null>(null)

// Game state
const activeGameIndex = ref<number | null>(null)
const currentScreenIndex = ref(0)
const completedGames = ref<number[]>([])

// Use composables
const {
  currentStep,
  partyData,
  validationResults,
  sampleConfig,
  initializeSampleConfig,
  validateConfiguration,
  generatePartyId,
  goToStep,
  joinUrl,
  tvJoinUrl,
  stepIndex
} = usePartyState()

const {
  isConnected,
  connectedPlayers,
  connectionError,
  currentPartyId,
  answerStats,
  initializeSignaling,
  createParty,
  startParty,
  startGame: hostStartGame,
  endGame,
  changeScreen,
  displayScoreUpdate,
  sendHostMessage,
  revealAnswers,
  setAutoRevealCallback,
  updateCurrentScreen,
  // Real-time features
  realTimeScoring,
  autoReveal,
  // Legacy methods
  sendPartyConfiguration,
  broadcastMessage,
  broadcastGameState,
  playerCount,
  readyPlayerCount,
  allPlayersReady
} = useHostSignaling(hostId.value)

// Debug: Check what realTimeScoring actually is
console.log('🔍 realTimeScoring in HostApp:', realTimeScoring)
console.log('🔍 realTimeScoring type:', typeof realTimeScoring)
console.log('🔍 realTimeScoring.value:', realTimeScoring?.value)

// Computed
const signaling = computed(() => ({
  broadcastMessage,
  playerCount: playerCount(),
  readyPlayerCount: readyPlayerCount()
}))

// Methods
function handleValidate(configJson: string) {
  console.log('🔧 Starting configuration validation...')
  console.log('📝 Config JSON:', configJson.substring(0, 200) + (configJson.length > 200 ? '...' : ''))

  try {
    const result = validateConfiguration(configJson)
    console.log('✅ Validation result:', result)

    if (result.success) {
      console.log('🎯 Configuration valid - activating party')
      // Send success to child component
      partyConfigRef.value?.handleValidationResult(true)
      // Success - activate party directly
      activateParty()
    } else {
      // Send error to child component
      partyConfigRef.value?.handleValidationResult(false, result.error)
      console.error('❌ Validation failed:', result.error)
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown validation error'
    // Send error to child component
    partyConfigRef.value?.handleValidationResult(false, errorMsg)
    console.error('💥 Validation threw exception:', error)
  }
}

async function activateParty() {
  if (!partyData.value) return

  // Generate unique party ID
  const partyId = generatePartyId()
  partyData.value.id = partyId

  try {
    // Initialize signaling with the new party ID
    await initializeSignaling(partyId)
    console.log('Host signaling initialized for party:', partyId)

    // Create party using new message structure
    createParty(partyId, partyData.value)
    console.log('Party creation request sent for party:', partyId)

    // Move to active party step
    goToStep('party-active')
  } catch (error) {
    console.error('Failed to activate party:', error)
    alert(`Failed to activate party: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

function startGame() {
  console.log('🎮 Starting party and transitioning to game management...')
  console.log('📊 HOST: Party data:', partyData.value)

  if (partyData.value && partyData.value.games) {
    console.log('🎉 HOST: Starting party with games:', partyData.value.games.length)
    startParty(partyData.value.games || [])

    // Transition to game-running step to show GameManager
    goToStep('game-running')
  } else {
    console.error('No party data or games to start')
  }

  // Open external game manager for testing
  // window.open('./integrated-round-manager.html?party=' + (partyData.value?.id || 'demo-party'), '_blank')
}

// Game management functions
function handleGameStart(gameIndex: number, gameData: any) {
  console.log('🎮 Starting game:', gameIndex, gameData)
  activeGameIndex.value = gameIndex
  currentScreenIndex.value = 0

  // Update current screen tracking for answer stats
  updateCurrentScreen(gameIndex, 0)

  hostStartGame(gameIndex, gameData.gameplaySlug, gameData)
}

function handleGameEnd(gameIndex: number) {
  console.log('🏁 Ending game:', gameIndex)
  if (!completedGames.value.includes(gameIndex)) {
    completedGames.value.push(gameIndex)
  }
  activeGameIndex.value = null
  currentScreenIndex.value = 0
  endGame(gameIndex, {})
}

function hostStartGameWrapper(gameIndex: number, gameType: string, gameData: any) {
  handleGameStart(gameIndex, { gameplaySlug: gameType, ...gameData })
}

function hostEndGameWrapper(gameIndex: number) {
  handleGameEnd(gameIndex)
}

function hostNextScreen() {
  if (activeGameIndex.value !== null) {
    const game = partyData.value?.games?.[activeGameIndex.value] as any
    const maxScreens = game?.screens?.length || 0
    const newScreenIndex = currentScreenIndex.value + 1

    if (newScreenIndex < maxScreens) {
      currentScreenIndex.value = newScreenIndex
      console.log('➡️ Next screen:', currentScreenIndex.value)

      // Update current screen tracking for answer stats
      updateCurrentScreen(activeGameIndex.value, currentScreenIndex.value)

      // Send change-screen message to server and players
      const screenData = game?.screens?.[currentScreenIndex.value] || {}
      changeScreen(activeGameIndex.value, currentScreenIndex.value, screenData)
    } else {
      console.log('⚠️ Cannot go to next screen: already at last screen')
    }
  }
}

function hostPreviousScreen() {
  if (activeGameIndex.value !== null && currentScreenIndex.value > 0) {
    currentScreenIndex.value--
    console.log('⬅️ Previous screen:', currentScreenIndex.value)

    // Update current screen tracking for answer stats
    updateCurrentScreen(activeGameIndex.value, currentScreenIndex.value)

    // Send change-screen message to server and players
    const game = partyData.value?.games?.[activeGameIndex.value] as any
    const screenData = game?.screens?.[currentScreenIndex.value] || {}
    changeScreen(activeGameIndex.value, currentScreenIndex.value, screenData)
  } else {
    console.log('⚠️ Cannot go to previous screen: already at first screen or no active game')
  }
}

function handleViewChange(view: string) {
  console.log('🖼️ Changing view to:', view)
  // For now, we could implement view changes by updating party state
  // or triggering specific host actions based on the view
  console.log('View change requested but not yet implemented:', view)
}

function handleRealTimeScoringUpdate(enabled: boolean) {
  console.log('⚡ Real-time scoring updated to:', enabled)
  realTimeScoring.value = enabled
}

function handleAutoRevealUpdate(enabled: boolean) {
  console.log('🎯 Auto-reveal updated to:', enabled)
  autoReveal.value = enabled
}

function changeView(view: string) {
  // Wrapper function for the GameManager's host signaling
  handleViewChange(view)
}

function testPartyOperations() {
  console.log('🧪 Testing Party Operations...')
  // Could open the separate round manager for testing
  // Round manager is now integrated into this component
  // window.open('./integrated-round-manager.html?party=' + (partyData.value?.id || 'demo-party'), '_blank')
}

function endParty() {
  if (confirm('Are you sure you want to end the party?')) {
    console.log('🚪 Ending party...')
    goToStep('configuration')
    partyData.value = null
    validationResults.value = null
    connectedPlayers.clear()
  }
}

// Initialize on mount
onMounted(async () => {
  console.log('🎮 Vue Host Interface Loading...')

  // Initialize with sample config
  initializeSampleConfig()

  // Set up auto-reveal callback connection
  setAutoRevealCallback(() => {
    if (gameManagerRef.value) {
      gameManagerRef.value.checkAndAutoReveal()
    }
  })

  // Finish loading
  loading.value = false

  console.log('✅ Vue Host Interface Loaded Successfully')
})
</script>

<style scoped>
.max-w-1400 {
  max-width: 1400px;
  margin: 0 auto;
}

.loading {
  text-align: center;
  padding: 50px;
  color: white;
}

.loading .spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #ffffff33;
  border-top: 5px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
